import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Typography, Box, Tabs, Tab, Button } from '@mui/material';
import AppDocuVehiclePage from './AppDocu/AppDocuVehiclePage';
import AppDocuCalibrationPage from './AppDocu/AppDocuCalibrationPage';
import AppDocuDocumentPage from './AppDocu/AppDocuDocumentPage';
import AppDocuServiceCall from './AppDocu/_Utils/AppDocuServiceCall';
import { notifySomething } from './AppDocu/_Utils/AppDocuCommon';
import LoadingSpinner from '../comp/gen/LoadingSpinner';
import UserAuthRequired from '../comp/gen/UserAuthRequired';
import AppDocuPacketPage from './AppDocu/AppDocuPacketPage';
import AppDocuGenDocumentPage from './AppDocu/AppDocuGenDocumentPage';

const AppDocuPage = (props) => {
  const [vehicles, setVehicles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [calibrations, setCalibrations] = useState([]);
  const [packets, setPackets] = useState([]);
  const [genDocuments, setGenDocuments] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [userState, setUserState] = useState("init");
  const [tabBarHeight, setTabBarHeight] = useState(0);
  const tabBarRef = useRef(null);
  const dataKey = ['vehicles', 'packets', 'calibrations', 'documents', 'genDocuments'];

  useEffect(() => {
    checkUser();
    // eslint-disable-next-line
  }, []);

  // Esegui il fetch ogni volta che si cambia tab e userState è valido
  useEffect(() => {
    if (userState !== "valid") return;
    fetchData(dataKey[tabIndex]);
  }, [tabIndex, userState]);

  useLayoutEffect(() => {
    if (tabBarRef.current) {
      setTabBarHeight(tabBarRef.current.offsetHeight);
    }
  }, [tabIndex, userState]); // aggiorna anche quando userState cambia

  useEffect(() => {
    // Cambia il titolo della pagina in base al tab selezionato
    const titles = [
      'Vehicles - AppDocu',
      'Packets - AppDocu',
      'Calibrations - AppDocu',
      'Documents - AppDocu',
      'Gen Documents - AppDocu'
    ];
    document.title = titles[tabIndex] || 'AppDocu';
  }, [tabIndex]);

  const checkUser = async () => {
    try {
      const res = await AppDocuServiceCall.checkUser();
      if (res.data && res.data.is_valid === false) {
        setUserState("notAllowed");
      } else {
        setUserState("valid");
      }
    } catch (error) {
      setUserState("error");
    }
  };
  
  // Funzione generica per fetchare dati in base al tipo
  const fetchData = async (type) => {
    const mapping = {
      vehicles: {
        service: () => AppDocuServiceCall.listVehicles(),
        setState: setVehicles,
        errorMsg: 'Error fetching vehicles',
        resultKey: 'machines',
      },
      packets: {
        service: () => AppDocuServiceCall.listPackets(),
        setState: setPackets,
        errorMsg: 'Error fetching packets',
        resultKey: 'packages',
      },
      calibrations: {
        service: () => AppDocuServiceCall.listCalibrations(),
        setState: setCalibrations,
        errorMsg: 'Error fetching calibrations',
        resultKey: 'calibrations',
      },
      documents: {
        service: () => AppDocuServiceCall.listDocuments(),
        setState: setDocuments,
        errorMsg: 'Error fetching documents',
        resultKey: 'documents',
      },
      genDocuments: {
        service: () => AppDocuServiceCall.listGenDocuments(),
        setState: setGenDocuments,
        errorMsg: 'Error fetching gen documents',
        resultKey: 'documents',
      },
    };
    const map = mapping[type];
    if (!map) return;
    try {
      const res = await map.service();
      if (res.data && res.data.is_valid === false) {
        notifySomething(res.data.message || map.errorMsg, 'error');
        return;
      }
      map.setState(res.data[map.resultKey]);
    } catch (error) {
      console.error(map.errorMsg + ':', error);
      notifySomething(map.errorMsg, 'error');
    }
  };
  
  // Funzione generica per resettare lo stato in base al tipo
  const resetData = (type) => {
    const mapping = {
      vehicles: setVehicles,
      packets: setPackets,
      calibrations: setCalibrations,
      documents: setDocuments,
      genDocuments: setGenDocuments,
    };
    if (mapping[type]) mapping[type]([]);
  };

  // Funzione per forzare il refresh del tab attivo
  const handleRefresh = async () => {
    dataKey.forEach(resetData);
    // sleep di 500ms per evitare problemi di rendering
    setTimeout(() => {
      fetchData(dataKey[tabIndex]);
    }, 500);
  };

  // Gestore custom per cambio tab: svuota lo stato PRIMA di cambiare tab
  const handleTabChange = (_, newIndex) => {
    dataKey[newIndex] && resetData(dataKey[newIndex]);
    setTabIndex(newIndex);    
  };
  
  if (userState === "init") return <LoadingSpinner />
  if (userState === "notAllowed") return <UserAuthRequired notallowed={true} />  
  if (userState === "error") return <UserAuthRequired text="Loading error." />  
  
  // Calcola l'altezza totale sommando navbarHeight (stringa tipo '84px') e tabBarHeight (numero)
  const navbarHeightPx = parseInt(props.navbarHeight, 10) || 0;
  const totalNavbarHeight = navbarHeightPx + tabBarHeight - 5 + 0;

  // Stile per massimizzare lo spazio dei tab e dei componenti
  const mainBoxStyle = {
    p: 0,
    m: 0,
    width: '100vw',
    minHeight: `calc(100vh - ${navbarHeightPx}px)`,
    position: 'relative',
    left: '50%',
    right: 0,
    transform: 'translateX(-50%)',
    background: 'transparent',
    boxSizing: 'border-box',
    overflow: 'auto',
  };

  return (
    <Box sx={mainBoxStyle}>
      <div ref={tabBarRef} style={{ background: 'white', zIndex: 2, display: 'flex', alignItems: 'center' }}>
        <Tabs 
          value={tabIndex} 
          onChange={handleTabChange} 
          sx={{ mb: 1, minHeight: 48, flex: 1 }}
        >
          <Tab label="Vehicles" />
          <Tab label="Packets" />
          <Tab label="Calibrations" />
          <Tab label="Documents" />
          <Tab label="Gen Documents" />
        </Tabs>
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
          <Button onClick={handleRefresh} variant="outlined" size="small" sx={{ mr: 2 }}>
            Ricarica
          </Button>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: 'text.secondary', whiteSpace: 'nowrap', textTransform: 'uppercase', mr: 3 }}>
            AppDocu Management
          </Typography>
        </Box>
      </div>
      <Box sx={{ width: '100%', overflow: 'auto', p: 0 }}>
        {tabIndex === 0 && (
          <AppDocuVehiclePage navbarHeight={totalNavbarHeight + "px"} vehicles={vehicles} onItemsChange={() => fetchData('vehicles')} />
        )}      
        {tabIndex === 1 && (
          <AppDocuPacketPage navbarHeight={totalNavbarHeight + "px"} packets={packets} onItemsChange={() => fetchData('packets')} />
        )}
        {tabIndex === 2 && (
          <AppDocuCalibrationPage navbarHeight={totalNavbarHeight + "px"} items={calibrations} onItemsChange={() => fetchData('calibrations')} />
        )}
        {tabIndex === 3 && (
          <AppDocuDocumentPage navbarHeight={totalNavbarHeight + "px"} items={documents} onItemsChange={() => fetchData('documents')} />
        )}
        {tabIndex === 4 && (
          <AppDocuGenDocumentPage navbarHeight={totalNavbarHeight + "px"} items={genDocuments} onItemsChange={() => fetchData('genDocuments')} />
        )}
      </Box>
    </Box>
  );
};

export default AppDocuPage;
